import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { fadeIn, fadeScale, springSnappy } from '@/lib/animations';
import { WEB_HOST, detectPlatform, shortcutHelp, type WorkspaceMode } from '@/lib/canvas/workspace-commands';

interface KeyboardShortcutsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: WorkspaceMode;
}

function KeyBadge({ label }: { label: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[22px] h-[22px] bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-foreground border border-border">
      {label}
    </kbd>
  );
}

export function KeyboardShortcutsOverlay({
  isOpen,
  onClose,
  mode = 'flow',
}: KeyboardShortcutsOverlayProps) {
  const groups = shortcutHelp(WEB_HOST, mode, detectPlatform());
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  return (
    <AnimatePresence>
      {isOpen && (
    <motion.div
      key="keyboard-shortcuts-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard Shortcuts"
    >
      <motion.div
        {...fadeIn}
        transition={springSnappy}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        {...fadeScale}
        transition={springSnappy}
        className="relative z-10 w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl bg-popover text-popover-foreground border border-border shadow-float mx-4"
      >
        <div className="sticky top-0 bg-popover border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {groups.map((group) => (
            <div key={group.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.shortcuts.map((shortcut) => (
                  <li
                    key={shortcut.id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-muted-foreground text-left">
                      {shortcut.description}{shortcut.available ? '' : ' · unavailable'}
                    </span>
                    <KeyBadge label={shortcut.keys} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border px-6 py-3 flex items-center justify-center">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            Press
            <KeyBadge label="⌘/" />
            to toggle this overlay
          </span>
        </div>
      </motion.div>
    </motion.div>
      )}
    </AnimatePresence>
  );
}
